/**
 * Entries Ledger — cross-org admin/delegate view of contestant entries
 * with payment status.
 *
 * Permissions:
 *   - admin (ALL-org): all orgs, all rodeos
 *   - admin (org-scoped): only orgs they administer
 *   - delegate: only orgs they hold the delegate role in
 *
 * Click a contestant row to deep-link into the user details page Events
 * tab so the per-user breakdown takes over from the cross-rodeo view.
 */

import { type ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import {
  Loader2,
  Filter,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  CreditCard,
  Building2,
  Banknote,
  FileCheck,
  Clock,
  User,
  Users,
  UserCheck,
  UserX,
  HelpCircle,
  Gift,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarkEntryPaidButton } from '../../components/admin/MarkEntryPaidButton';
import { EntryAdminActions } from '../../components/admin/EntryAdminActions';
import { Badge } from '../../components/ui/badge';
import {
  ProviderBadge,
  deriveProvider,
} from '../../components/financials/ProviderBadge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAdminOrganizations, useImpersonateUser } from '../../api/extensions/useAdminOps';
import { useRodeosList } from '../../api/extensions/useRodeoOps';
import {
  useEventsClient,
  eventsKeys,
} from '../../api/generated_hooks/generated-events-hooks';
import { useOrganizationsList } from '../../api/generated_hooks/generated-organizations-hooks';
import {
  useAdminEntriesLedger,
  type AdminEntryRow,
  type LedgerCreatedByRole,
  type LedgerPartnerStatus,
  type LedgerPaymentMethod,
  type PaymentStatus,
} from '../../api/extensions/useAdminEntries';
import { Pagination } from '../../components/Pagination';
import { useAuthStore } from '../../state_management_and_stores/auth.store';
import { authService } from '../../services/auth.service';
import { resetClientState } from '../../lib/resetClientState';
import { notify } from '../../utils/notifications';
import { useDebounce } from '../../hooks/useDebounce';
// Standardized search-term highlighter — same util used by ManageUsersPage,
// PartnerSearchCombobox, etc. Pairs with the .search-highlight class loaded
// globally from src/index.css → src/styles/search-highlight.css.
import { highlightSearchTerms } from '../../../../../config/search/search-utils';
import { getDisplayNameSupplement } from '../../utils/adminNameDisplay';

const ALL_ORG_ID = '00000000-0000-0000-0000-000000000001';
const ALL_SENTINEL = '__all__';

// Columns the user can sort by. Action / Actions cells aren't sortable. The
// default — and what the user explicitly asked us to preserve — is
// `created_at desc` (newest entries first).
type SortKey =
  | 'contestant'
  | 'event'
  | 'rodeo'
  | 'created_at'
  | 'last_updated_at'
  | 'fee'
  | 'paid'
  | 'status';
type SortOrder = 'asc' | 'desc';

// Default sort direction per column. Dates and money sort desc on first click
// (biggest / latest first feels right); text columns sort asc (A→Z).
const DEFAULT_ORDER: Record<SortKey, SortOrder> = {
  contestant: 'asc',
  event: 'asc',
  rodeo: 'asc',
  created_at: 'desc',
  last_updated_at: 'desc',
  fee: 'desc',
  paid: 'desc',
  status: 'asc',
};

function formatCurrency(amount: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

const STATUS_META: Record<PaymentStatus, { label: string; cls: string; rowCls: string }> = {
  paid: {
    label: 'Paid',
    cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    rowCls: 'bg-emerald-500/[0.04]',
  },
  unpaid: {
    label: 'Unpaid',
    cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
    rowCls: 'bg-rose-500/[0.06]',
  },
  partial: {
    label: 'Partial',
    cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    rowCls: 'bg-amber-500/[0.06]',
  },
  // In-flight ACH/bank debit — money submitted to Stripe, not yet cleared.
  // Blue (not rose) so admins don't read it as an unpaid balance to chase.
  processing: {
    label: 'Processing',
    cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30',
    rowCls: 'bg-sky-500/[0.05]',
  },
  // ACH micro-deposit awaiting the contestant's bank verification. Indigo (not
  // rose) so admins read it as in-progress, not an unpaid balance to chase.
  verifying: {
    label: 'Verifying bank',
    cls: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
    rowCls: 'bg-indigo-500/[0.05]',
  },
  free: {
    label: 'Free',
    cls: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30',
    rowCls: '',
  },
};

export default function EntriesLedgerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAllOrgAdmin = useAuthStore((s) => s.isAllOrgAdmin());
  const role = useAuthStore((s) => s.role);
  const userRoleName = role?.role_name;
  const isAdmin = userRoleName === 'admin';
  const setAuthData = useAuthStore((s) => s.setAuthData);
  // Used to suppress the impersonate icon on the admin's own ledger rows.
  const currentUserProfileId = useAuthStore((s) => s.profile?.id);
  const impersonateUser = useImpersonateUser();

  /**
   * Start impersonating the contestant on a ledger row. Mirrors the flow in
   * ManageUsersPage: reset cached admin-scoped data and bounce to /dashboard
   * BEFORE flipping the auth store, so the route guard doesn't race us to
   * /unauthorized the moment the store sees the impersonated "user" role.
   */
  const handleImpersonate = useCallback(
    async (entry: AdminEntryRow) => {
      if (!entry.user_profile_id) return;
      try {
        const result = await impersonateUser.mutateAsync(entry.user_profile_id);
        resetClientState(queryClient);
        navigate('/dashboard', { replace: true });
        const session = await authService.validateSession();
        setAuthData({
          user: session.user,
          profile: session.profile,
          role: session.role,
          permissions: session.permissions,
          impersonator: session.impersonator ?? null,
        });
        notify.success(`Now impersonating ${result.targetName}`);
      } catch (err: any) {
        notify.error(err?.message || 'Failed to impersonate user');
      }
    },
    [impersonateUser, queryClient, navigate, setAuthData],
  );

  // Filter state — all driven through query params so the filtered view is
  // shareable / refreshable. Empty string everywhere maps to "no filter".
  const orgId = searchParams.get('org') ?? '';
  const rodeoId = searchParams.get('rodeo') ?? '';
  const eventId = searchParams.get('event') ?? '';
  const paymentStatus = (searchParams.get('status') ?? '') as
    | 'paid'
    | 'unpaid'
    | 'partial'
    | 'processing'
    | 'verifying'
    | '';
  const [search, setSearch] = useState('');
  // 300ms matches globalSearchConfig.debounceMs and the inline pattern in
  // ManageUsersPage so search feels consistent across admin tools.
  const debouncedSearch = useDebounce(search, 300);

  // Sort state — defaults to newest-entered first so the page matches the
  // previous behavior on first paint. Clicking a column toggles direction;
  // clicking a different column resets direction to that column's natural
  // default (text asc, money/date desc).
  const [sortBy, setSortBy] = useState<SortKey>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Server-side pagination state. The list is fetched one page at a time —
  // mirrors the Manage Users page — so a growing ledger no longer has to
  // render thousands of rows at once. Any filter / search / sort change resets
  // back to page 1 so the user isn't stranded on a now-out-of-range page.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Row-expansion state. Multiple rows can be open at once so admins can
  // compare details across entries side-by-side (e.g. checking whether
  // two teammates' entries were both paid by the same partner). Keyed by
  // registration_id which is stable across re-fetches.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder(DEFAULT_ORDER[key]);
    }
    setPage(1);
  };

  const updateFilter = (key: 'org' | 'rodeo' | 'event' | 'status', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== ALL_SENTINEL) next.set(key, value);
    else next.delete(key);
    // Changing org clears rodeo + event filters since they're scoped to it.
    if (key === 'org') {
      next.delete('rodeo');
      next.delete('event');
    }
    if (key === 'rodeo') next.delete('event');
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  // Org dropdown source: ALL-org admins get every real org (cheap to load
  // and good UX); scoped admins/delegates see only their administered orgs.
  // We use `useAdminOrganizations` for both scoped admin and delegate —
  // the backend returns the orgs the actor has admin role in. For
  // delegates, that endpoint also filters by their role; if it doesn't,
  // the backend ledger query still enforces the scope so a curious delegate
  // can't escape their bounds.
  const { data: allOrgsData } = useOrganizationsList();
  const { data: adminOrgsData } = useAdminOrganizations();
  const orgOptions = useMemo(() => {
    if (isAllOrgAdmin && Array.isArray(allOrgsData)) {
      return (allOrgsData as any[])
        .filter((o) => o.id !== ALL_ORG_ID)
        .map((o) => ({ id: o.id as string, name: (o.short_name || o.name) as string }));
    }
    return (adminOrgsData?.orgs ?? [])
      .filter((o) => o.id !== ALL_ORG_ID)
      .map((o: any) => ({ id: o.id, name: (o.short_name || o.name) as string }));
  }, [isAllOrgAdmin, allOrgsData, adminOrgsData]);

  // Rodeo dropdown — only fetch when an org is selected. Without a filter
  // we'd be pulling every rodeo across every org which makes for a useless
  // list and a noisy query. page_size is capped server-side at 100; we ask
  // for the cap so a single org's full season fits in one round-trip.
  const { data: rodeosData } = useRodeosList({
    organization_id: orgId || undefined,
    page_size: 100,
    enabled: !!orgId,
  });
  const rodeoOptions = useMemo(() => {
    return (rodeosData?.data ?? []).map((r: any) => ({ id: r.id, name: r.name }));
  }, [rodeosData]);

  // Reset to the first page whenever the search term settles on a new value —
  // otherwise a search run while sitting on page 5 could land out of range.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, error } = useAdminEntriesLedger({
    orgId: orgId || undefined,
    rodeoId: rodeoId || undefined,
    eventId: eventId || undefined,
    paymentStatus: paymentStatus || undefined,
    search: debouncedSearch.trim() || undefined,
    page,
    pageSize,
    sortBy,
    sortOrder,
  });

  // Server returns exactly the page to render — already filtered (org / rodeo
  // / event / status / search), sorted, and sliced. No client-side filter or
  // sort pass: with pagination those would only ever see the current page.
  const entries = data?.entries ?? [];

  // Event dropdown options. Previously derived from the returned rows, but with
  // pagination a single page won't contain every event — so we pull the full
  // event list for the selected rodeo directly. Gated on `rodeoId` so it only
  // fires once a rodeo is chosen (the dropdown is disabled until then), never
  // pulling the unscoped all-events list on initial load.
  const eventsClient = useEventsClient();
  const { data: rodeoEventsData } = useQuery({
    queryKey: eventsKeys.list({ rodeo_id: rodeoId }),
    queryFn: () => eventsClient.getAll({ filters: { rodeo_id: rodeoId } }),
    enabled: !!rodeoId,
  });
  const eventOptions = useMemo(() => {
    const rows = (rodeoEventsData ?? []) as any[];
    return rows.map((e) => ({
      id: e.id as string,
      name: (e.event_name || e.trig_event_type_name || 'Unnamed event') as string,
    }));
  }, [rodeoEventsData]);

  const totals = data?.totals;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Entries Ledger</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {userRoleName === 'delegate'
            ? "Contestants entered in your organizations' rodeos with payment status."
            : 'Contestants entered in rodeos and events with payment status.'}{' '}
          Click a row to open that contestant's full entry history.
        </p>
      </div>

      {/* Filters */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-muted-foreground">
          <Filter className="h-4 w-4" />
          Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Organization
            </label>
            <Select
              value={orgId || ALL_SENTINEL}
              onValueChange={(v) => updateFilter('org', v)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All organizations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SENTINEL}>All organizations</SelectItem>
                {orgOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Rodeo
            </label>
            <Select
              value={rodeoId || ALL_SENTINEL}
              onValueChange={(v) => updateFilter('rodeo', v)}
              disabled={!orgId}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder={orgId ? 'All rodeos' : 'Pick an org first'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SENTINEL}>All rodeos</SelectItem>
                {rodeoOptions.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Event
            </label>
            <Select
              value={eventId || ALL_SENTINEL}
              onValueChange={(v) => updateFilter('event', v)}
              disabled={!rodeoId || eventOptions.length === 0}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder={rodeoId ? 'All events' : 'Pick a rodeo first'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SENTINEL}>All events</SelectItem>
                {eventOptions.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Payment status
            </label>
            <Select
              value={paymentStatus || ALL_SENTINEL}
              onValueChange={(v) => updateFilter('status', v)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SENTINEL}>All statuses</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="verifying">Verifying bank</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Search
            </label>
            <Input
              placeholder="Name, email, event…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9"
            />
          </div>
        </div>
      </div>

      {/* Totals */}
      {totals && (() => {
        // Outstanding is summed server-side per non-cancelled row so a
        // cancelled+refunded entry's retained Stripe fee can't quietly
        // reduce what active contestants still owe. `?? 0` guards new
        // fields so a stale backend renders $0 instead of NaN until the
        // server catches up. The fallback still subtracts refunds because it
        // only fires against a backend old enough to omit totalOutstanding —
        // which is also old enough to report totalPaid as lifetime gross.
        const totalRefunded = totals.totalRefunded ?? 0;
        const outstanding =
          totals.totalOutstanding ??
          Math.max(0, totals.totalDue - (totals.totalPaid - totalRefunded));
        return (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <SummaryCard
              label="Contestants"
              value={String(totals.contestantCount)}
              hint={`${totals.registrationCount} entries`}
            />
            <SummaryCard
              label="Total fees due"
              value={formatCurrency(totals.totalDue)}
              accent="text-amber-700 dark:text-amber-300"
              hint="incl. already paid"
            />
            <SummaryCard
              label="Total paid"
              value={formatCurrency(totals.totalPaid)}
              accent="text-emerald-700 dark:text-emerald-300"
              // Headline is net so the row reconciles against Total fees due;
              // the hint carries lifetime gross so both figures are on the
              // card rather than the reader having to add Refunded back.
              hint={
                totalRefunded > 0.01
                  ? `${formatCurrency(totals.totalPaid + totalRefunded)} paid, less refunds`
                  : 'net of refunds'
              }
            />
            <SummaryCard
              label="Refunded"
              value={formatCurrency(totalRefunded)}
              accent={
                totalRefunded > 0.01
                  ? 'text-rose-700 dark:text-rose-300'
                  : 'text-foreground'
              }
            />
            <SummaryCard
              label="Outstanding"
              value={formatCurrency(outstanding)}
              accent={
                outstanding > 0.01
                  ? 'text-rose-700 dark:text-rose-300'
                  : 'text-foreground'
              }
              hint={`${totals.unpaidCount} unpaid · ${totals.partialCount} partial${
                totals.processingCount
                  ? ` · ${totals.processingCount} processing`
                  : ''
              }${
                totals.verifyingCount
                  ? ` · ${totals.verifyingCount} verifying`
                  : ''
              }`}
            />
          </div>
        );
      })()}

      {/* Table */}
      <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
        {error ? (
          <div className="p-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-destructive">Couldn't load entries</h3>
              <p className="text-sm text-destructive/80 mt-1">
                {(error as Error).message || 'Unknown error'}
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            No entries match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <SortableHeader
                    label="Contestant"
                    sortKey="contestant"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Event"
                    sortKey="event"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Rodeo / Org"
                    sortKey="rodeo"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Entered"
                    sortKey="created_at"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Updated"
                    sortKey="last_updated_at"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Fee"
                    sortKey="fee"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Paid"
                    sortKey="paid"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y">
                {entries.map((entry) => (
                  <LedgerRow
                    key={entry.registration_id}
                    entry={entry}
                    canDeepLink={userRoleName === 'admin'}
                    canImpersonate={isAdmin}
                    currentUserProfileId={currentUserProfileId}
                    onImpersonate={handleImpersonate}
                    impersonatePendingId={
                      impersonateUser.isPending
                        ? (impersonateUser.variables as string)
                        : null
                    }
                    searchQuery={debouncedSearch}
                    expanded={expandedRows.has(entry.registration_id)}
                    onToggle={toggleRow}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination — server-driven. Hidden while loading the first page (no
          totals yet) and when everything fits on a single page. */}
      {!error && data && data.total > 0 && (
        <Pagination
          currentPage={data.page}
          totalPages={Math.max(1, Math.ceil(data.total / data.pageSize))}
          totalItems={data.total}
          pageSize={data.pageSize}
          hasNextPage={data.hasMore}
          hasPrevPage={data.page > 1}
          onPageChange={(newPage) => {
            setPage(newPage);
            setExpandedRows(new Set());
          }}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
            setExpandedRows(new Set());
          }}
          variant="default"
          showPageSizeSelector
          showItemCount
          showJumpTo={false}
          className="justify-center sm:justify-between"
        />
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent?: string;
  hint?: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4 flex flex-col">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex-1 flex flex-col items-center justify-center text-center mt-2">
        <div className={`text-2xl font-bold tabular-nums ${accent ?? ''}`}>{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
    </div>
  );
}

/**
 * Sortable column header. Click toggles direction when this is the active
 * column, otherwise picks up the column's natural default. Keyboard-activatable
 * (Enter / Space) and exposes `aria-sort` for screen readers.
 */
function SortableHeader({
  label,
  sortKey,
  sortBy,
  sortOrder,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey;
  sortOrder: SortOrder;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const isActive = sortBy === sortKey;
  const ariaSort = isActive
    ? sortOrder === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      tabIndex={0}
      role="button"
      onClick={() => onSort(sortKey)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort(sortKey);
        }
      }}
      className={`font-medium px-4 py-2 cursor-pointer select-none transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <span
        className={`inline-flex items-center gap-1 ${
          align === 'right' ? 'flex-row-reverse' : ''
        }`}
      >
        {label}
        <SortArrow active={isActive} order={sortOrder} />
      </span>
    </th>
  );
}

function SortArrow({ active, order }: { active: boolean; order: SortOrder }) {
  if (!active) {
    return (
      <svg
        aria-hidden
        className="h-3.5 w-3.5 text-muted-foreground/40"
        viewBox="0 0 16 16"
        fill="currentColor"
      >
        <path d="M8 3l4 5H4l4-5zm0 10l-4-5h8l-4 5z" />
      </svg>
    );
  }
  return order === 'asc' ? (
    <svg
      aria-hidden
      className="h-3.5 w-3.5 text-foreground"
      viewBox="0 0 16 16"
      fill="currentColor"
    >
      <path d="M8 4l4 5H4l4-5z" />
    </svg>
  ) : (
    <svg
      aria-hidden
      className="h-3.5 w-3.5 text-foreground"
      viewBox="0 0 16 16"
      fill="currentColor"
    >
      <path d="M8 12l-4-5h8l-4 5z" />
    </svg>
  );
}

// Exported for unit testing (admin-gated impersonate icon). The page itself
// renders it via the default export below.
export function LedgerRow({
  entry,
  canDeepLink,
  canImpersonate,
  currentUserProfileId,
  onImpersonate,
  impersonatePendingId,
  searchQuery,
  expanded,
  onToggle,
}: {
  entry: AdminEntryRow;
  canDeepLink: boolean;
  canImpersonate: boolean;
  currentUserProfileId?: string;
  onImpersonate: (entry: AdminEntryRow) => void;
  impersonatePendingId: string | null;
  searchQuery: string;
  expanded: boolean;
  onToggle: (registrationId: string) => void;
}) {
  const meta = STATUS_META[entry.payment_status];
  const cancelled = entry.is_cancelled;
  const rowCls = [
    meta.rowCls,
    cancelled ? 'opacity-50' : '',
    'cursor-pointer hover:bg-muted/30 transition-colors',
  ]
    .filter(Boolean)
    .join(' ');
  // Render a string-or-fallback through the global highlighter so matched
  // substrings get the .search-highlight class. `highlightSearchTerms`
  // returns the original string when no query is set, which is cheap.
  const hl = (s: string | null | undefined, fallback: string = '—') =>
    s ? highlightSearchTerms(s, searchQuery) : fallback;
  const handleRowClick = () => onToggle(entry.registration_id);
  // The action cell wraps interactive children (View link) — clicks there
  // must NOT toggle expansion or the user can't deep-link.
  const stopProp = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <>
    <tr
      className={rowCls}
      onClick={handleRowClick}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowClick();
        }
      }}
    >
      <td className="px-4 py-2.5">
        <div className="flex items-start gap-2">
          <span
            aria-hidden
            className="mt-0.5 text-muted-foreground shrink-0"
            title={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </span>
          <div className="min-w-0">
        {/* Legal name is the admin-list default; display name is shown
            underneath as an "aka" line when the user has opted in to one
            that differs from their legal name. Fall back to the composed
            contestant_name (server's legal-first string) for old rows
            that haven't been re-fetched yet. */}
        <div className="flex items-center gap-1.5">
          <div className="font-medium">
            {hl(
              [entry.legal_first_name, entry.legal_last_name]
                .filter(Boolean)
                .join(' ') || entry.contestant_name,
            )}
          </div>
          {/* Admins can jump straight into impersonating this contestant from
              the ledger. Hidden for non-admins, for rows missing a user id,
              and for the admin's own rows (can't impersonate yourself). */}
          {canImpersonate &&
            entry.user_profile_id &&
            entry.user_profile_id !== currentUserProfileId && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onImpersonate(entry);
                }}
                disabled={impersonatePendingId !== null}
                aria-label="Impersonate user (sign in as them to debug what they see)"
                title="Impersonate user (sign in as them to debug what they see)"
                className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50 shrink-0"
              >
                {impersonatePendingId === entry.user_profile_id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserCircleIcon className="h-4 w-4" />
                )}
              </button>
            )}
        </div>
        {(() => {
          const supplement = getDisplayNameSupplement(entry);
          if (!supplement) return null;
          return (
            <div
              className="text-xs text-muted-foreground italic"
              title="Display name (set by the user)"
            >
              aka {hl(supplement)}
            </div>
          );
        })()}
        {entry.contestant_email && (
          <div className="text-xs text-muted-foreground">
            {hl(entry.contestant_email)}
          </div>
        )}
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="font-medium">
          {hl(entry.event_name || entry.event_type)}
        </div>
        <div className="text-xs text-muted-foreground space-x-2">
          {entry.position && <span>{entry.position}</span>}
          {entry.partner_name && <span>w/ {entry.partner_name}</span>}
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium">{hl(entry.rodeo_name)}</span>
          {/* The RODEO was cancelled (not just this entry). Without this the
              row's $0 balance looks like a bug — this is why it owes nothing. */}
          {entry.rodeo_cancelled ? (
            <span
              className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-destructive/10 text-destructive"
              title="This rodeo was cancelled. Its entries owe nothing and can be cancelled even after the draw."
            >
              Rodeo cancelled
            </span>
          ) : null}
        </div>
        <div className="text-xs text-muted-foreground">
          {entry.organization_name ? hl(entry.organization_name, '') : ''}
        </div>
      </td>
      <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">
        {/* Two-line layout matches the rest of the table's "primary +
            secondary" pattern. Date on top, time below in smaller text. */}
        <div className="text-sm">
          {new Date(entry.created_at).toLocaleDateString(undefined, {
            year: '2-digit',
            month: 'numeric',
            day: 'numeric',
          })}
        </div>
        <div className="text-xs">
          {new Date(entry.created_at).toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </div>
      </td>
      <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">
        {/* Derived "last activity" timestamp from the server. Same two-line
            layout as Entered. Fall back to created_at when the backend
            didn't send a last_updated_at (older clients / partial rollout). */}
        {(() => {
          const ts = entry.last_updated_at || entry.created_at;
          return (
            <>
              <div className="text-sm">
                {new Date(ts).toLocaleDateString(undefined, {
                  year: '2-digit',
                  month: 'numeric',
                  day: 'numeric',
                })}
              </div>
              <div className="text-xs">
                {new Date(ts).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </div>
            </>
          );
        })()}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {entry.amount_due > 0 ? formatCurrency(entry.amount_due) : '—'}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {/* Paid cell shows lifetime gross when present so cancelled-but-once-paid
            entries don't collapse to "—". A refund (or debit adjustment) is
            surfaced on a second line so the row reads as "$100 paid,
            $100 refunded" instead of silently zeroing out. */}
        {entry.amount_paid_gross > 0.01 ? (
          <div className="flex flex-col items-end leading-tight">
            <span>{formatCurrency(entry.amount_paid_gross)}</span>
            {entry.amount_refunded > 0.01 && (
              <span className="text-xs text-rose-700 dark:text-rose-300">
                −{formatCurrency(entry.amount_refunded)} refunded
              </span>
            )}
          </div>
        ) : (
          '—'
        )}
      </td>
      <td className="px-4 py-2.5">
        {/* Cancelled rows: render two stacked badges so the reader sees both
            "this was cancelled" AND the historical payment state ("did they
            actually pay before backing out?"). Non-cancelled rows keep the
            single payment-status badge they had before. */}
        {cancelled ? (
          <div className="flex flex-col gap-1 items-start">
            <Badge
              variant="outline"
              className="text-xs bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"
            >
              Cancelled
            </Badge>
            <Badge
              variant="outline"
              className={`text-xs ${
                entry.was_paid ? STATUS_META.paid.cls : STATUS_META.unpaid.cls
              }`}
            >
              {entry.was_paid ? 'Paid' : 'Unpaid'}
            </Badge>
          </div>
        ) : (
          // text-center + justify-center keep multi-word labels that wrap
          // (e.g. "Verifying bank") balanced instead of left-aligned with a
          // ragged right gap.
          <Badge
            variant="outline"
            className={`text-xs text-center justify-center ${meta.cls}`}
          >
            {meta.label}
          </Badge>
        )}
      </td>
      <td className="px-4 py-2.5 text-right" onClick={stopProp}>
        {canDeepLink && entry.user_profile_id && (
          <Button asChild size="sm" variant="ghost" className="h-7 px-2">
            <Link to={`/users/${entry.user_profile_id}?tab=entries`}>
              View
              <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
          </Button>
        )}
      </td>
    </tr>
    {expanded && (
      <tr className="bg-muted/20">
        {/* colspan matches the 9 columns above (8 sortable + the action cell).
            Keeping the detail panel inside a single tr lets it animate as
            one unit and preserves the per-row hover/focus parent. */}
        <td colSpan={9} className="px-6 py-4 border-t border-border/60">
          <EntryDetailsPanel entry={entry} />
        </td>
      </tr>
    )}
    </>
  );
}

// ─── Payment-method + creator-role display helpers ────────────────────────
// Centralised here so the detail panel is the only thing that needs to
// know how a raw `payment_method` / `role` string surfaces in the UI. If
// we add another method or role later the change is one place.
const PAYMENT_METHOD_META: Record<
  LedgerPaymentMethod,
  { label: string; icon: typeof CreditCard }
> = {
  stripe_card: { label: 'Stripe (card)', icon: CreditCard },
  bank: { label: 'Bank (ACH)', icon: Building2 },
  cash: { label: 'Cash', icon: Banknote },
  check: { label: 'Check', icon: FileCheck },
  pay_later: { label: 'Pay later', icon: Clock },
};

const CREATOR_ROLE_META: Record<LedgerCreatedByRole, string> = {
  self: 'signed themselves up',
  buddy_group_primary: 'buddy group primary',
  team_roping_member: 'team roping partner',
};

// ─── Team-roping partner status display ───────────────────────────────────
// Maps the registration's canonical `partner_status` to a human label + badge
// colour for the partner card. Wording mirrors how the team describes these
// states elsewhere: "Waiting approval" for an outstanding invite, "Declined"
// for a partner who rejected, etc. 'none' isn't listed here — that's the
// solo/admin case, which the card renders separately (no badge).
const PARTNER_STATUS_META: Record<
  Exclude<LedgerPartnerStatus, 'none'>,
  { label: string; cls: string; icon: typeof UserCheck }
> = {
  pending: {
    label: 'Waiting approval',
    cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    icon: Clock,
  },
  approved: {
    label: 'Approved',
    cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    icon: UserCheck,
  },
  rejected: {
    label: 'Declined',
    cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
    icon: UserX,
  },
  cancelled: {
    label: 'Cancelled',
    cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
    icon: UserX,
  },
  needs_partner: {
    label: 'Needs partner',
    cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    icon: Users,
  },
};

/**
 * Team-roping partner card. Rendered in the expanded row panel only when the
 * entry belongs to a team-roping event. Shows the *latest* partner (the
 * backend surfaces the registration row's in-place partner fields, so a
 * previously-rejected-then-replaced partner never appears here), their role
 * (Header / Heeler — the opposite of the contestant's own position), and the
 * invitation status. Entries created solo (no partner selected) render a
 * "Solo / admin entry" state instead of an empty partner.
 */
function PartnerCard({ entry }: { entry: AdminEntryRow }) {
  const status = entry.partner_status ?? null;
  const hasPartner = !!entry.partner_name;
  // Solo/admin: a single entry with no partner. 'none' is the explicit solo
  // marker; a missing status with no partner name is treated the same so
  // legacy rows still read sensibly.
  const isSolo = status === 'none' || (!status && !hasPartner);
  const meta = status && status !== 'none' ? PARTNER_STATUS_META[status] : null;

  // The contestant's own position is on the row; the partner ropes the other
  // half of the team. Null when the position isn't recorded.
  const partnerPosition =
    entry.position === 'Header'
      ? 'Heeler'
      : entry.position === 'Heeler'
        ? 'Header'
        : null;

  return (
    <div className="rounded-md border border-border bg-card/60 p-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        <Users className="h-4 w-4" />
        <span>Team roping partner</span>
      </div>
      {isSolo ? (
        <div className="flex items-center gap-2 text-sm">
          <User className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium">Solo / admin entry</span>
          <span className="text-xs text-muted-foreground">
            Entered without a partner.
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <div className="text-sm font-medium">
            {entry.partner_name || 'Partner not yet selected'}
          </div>
          {partnerPosition && (
            <span className="text-xs text-muted-foreground">
              roping as {partnerPosition}
            </span>
          )}
          {meta && (
            <Badge variant="outline" className={`text-xs ${meta.cls}`}>
              <meta.icon className="h-3 w-3 mr-1" />
              {meta.label}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

function EntryDetailsPanel({ entry }: { entry: AdminEntryRow }) {
  const pm = entry.payment_method
    ? PAYMENT_METHOD_META[entry.payment_method]
    : null;
  // A $0 entry collects nothing, so the method it was signed up under is
  // history rather than a standing obligation. Announcing "Pay later —
  // contestant chose to settle in person" beside "No fee due on this entry"
  // contradicts it, so the signup choice moves into the hint.
  //
  // 'free' covers BOTH an admin waive and an event that never charged, and the
  // row carries no override flag to tell them apart — so the copy must hold
  // for either.
  const isFree = entry.payment_status === 'free';
  const PmIcon = isFree ? Gift : (pm?.icon ?? HelpCircle);
  // Resolve which "money side" cell to render: paid_by when settled,
  // responsible_party otherwise. Backend guarantees these are mutually
  // exclusive but we treat them defensively here so a stale client never
  // double-renders.
  const isPaid = !!entry.paid_by;
  // Was this entry paid by someone other than the contestant? Drives the
  // "Paid by partner" emphasis on team-roping entries where the partner
  // covered the fee — the row's user_profile_id matches the contestant,
  // and paid_by.user_id is whoever actually swiped the card.
  const paidByPartner =
    !!entry.paid_by &&
    !!entry.user_profile_id &&
    entry.paid_by.user_id !== entry.user_profile_id;
  return (
    <div className="space-y-4">
      {/* Team-roping entries lead with the partner card so the reader sees the
          current entry's partner + status at a glance. Non-team-roping entries
          skip it entirely. */}
      {entry.is_team_roping && <PartnerCard entry={entry} />}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <DetailCard
          icon={<PmIcon className="h-4 w-4" />}
          label="Payment method"
          value={isFree ? 'None needed' : (pm?.label ?? 'Unknown')}
          hint={
            isFree
              ? `Entry totals $0 — nothing to collect.${
                  pm ? ` Chose ${pm.label} at signup.` : ''
                }`
              : entry.payment_method === 'pay_later'
              ? 'Contestant chose to settle in person.'
              : entry.payment_method === 'cash'
                ? 'Recorded as a cash payment.'
                : entry.payment_method === 'check'
                  ? 'Recorded as a check payment.'
                  : entry.payment_status === 'verifying'
                  ? 'ACH bank verification in progress — contestant must confirm two micro-deposits before the debit is submitted.'
                  : entry.payment_status === 'processing'
                    ? 'Payment submitted — funds still clearing (bank transfers take ~4 business days).'
                    : entry.payment_method === 'bank'
                      ? 'Settled online via Stripe (bank transfer / ACH).'
                      : entry.payment_method
                        ? 'Settled online via Stripe.'
                        : 'No method recorded on this entry.'
          }
        />
        <DetailCard
          icon={<User className="h-4 w-4" />}
          label="Created by"
          value={entry.created_by?.name ?? 'Unknown'}
          hint={
            entry.created_by
              ? `as ${CREATOR_ROLE_META[entry.created_by.role]}`
              : 'No creator recorded on this entry.'
          }
          // Highlight the creator-role context with a distinct icon when the
          // entry was created on someone else's behalf.
          accentIcon={
            entry.created_by?.role === 'buddy_group_primary' ? (
              <Users className="h-3.5 w-3.5" />
            ) : entry.created_by?.role === 'team_roping_member' ? (
              <UserCheck className="h-3.5 w-3.5" />
            ) : null
          }
        />
        {isPaid ? (
          <DetailCard
            icon={<UserCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
            label="Paid by"
            value={entry.paid_by!.name}
            // Surface the partner-paid case explicitly. Otherwise we just
            // confirm the entry has cleared.
            hint={
              paidByPartner
                ? 'Team-roping partner covered this entry.'
                : "This entry's fee has cleared."
            }
            accentIcon={paidByPartner ? <Users className="h-3.5 w-3.5" /> : null}
            tone="paid"
          />
        ) : (
          <DetailCard
            icon={<User className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
            label="Responsible for payment"
            value={entry.responsible_party?.name ?? 'Unknown'}
            hint={
              entry.responsible_party
                ? entry.amount_due > 0
                  ? 'Owed on this entry until paid.'
                  : 'No fee due on this entry.'
                : 'No responsible party recorded.'
            }
            tone="unpaid"
          />
        )}
      </div>
      <FeeBreakdownPanel entry={entry} />
      <TenderSplitPanel entry={entry} />
      <MarkEntryPaidButton
        registrationId={entry.registration_id}
        paymentStatus={entry.payment_status}
        isCancelled={entry.is_cancelled}
        amountDue={entry.amount_due}
        feeBreakdown={entry.fee_breakdown}
      />
      <EntryAdminActions
        registrationId={entry.registration_id}
        paymentStatus={entry.payment_status}
        isCancelled={entry.is_cancelled}
        rodeoCancelled={entry.rodeo_cancelled}
        wasPaid={entry.was_paid}
        feeBreakdown={entry.fee_breakdown}
      />
    </div>
  );
}

// Split-tender breakdown — only when part of the entry was settled with org
// account credit at checkout. Surfaces "Stripe $130 + HighCall credit $25" so
// the settlement doesn't read as a single opaque online payment.
function TenderSplitPanel({ entry }: { entry: AdminEntryRow }) {
  const credit = entry.credit_applied ?? 0;
  if (credit <= 0) return null;
  const cardPortion = Math.max(0, (entry.amount_paid_gross ?? 0) - credit);
  const provider = deriveProvider(entry.payment_method) ?? 'stripe';
  const pm = entry.payment_method
    ? PAYMENT_METHOD_META[entry.payment_method]
    : null;
  return (
    <div className="rounded-md border border-border bg-card/60 p-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        <CircleDollarSign className="h-4 w-4" />
        <span>Paid with</span>
      </div>
      <div className="space-y-1.5">
        {cardPortion > 0 && (
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <ProviderBadge provider={provider} />
              {pm?.label ?? (provider === 'stripe' ? 'Card / bank' : 'Offline')}
            </span>
            <span className="tabular-nums font-medium">
              {formatCurrency(cardPortion)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <ProviderBadge provider="highcall" />
            Account credit
          </span>
          <span className="tabular-nums font-medium text-emerald-700 dark:text-emerald-300">
            {formatCurrency(credit)}
          </span>
        </div>
      </div>
    </div>
  );
}

// Fee breakdown row — shows what the contestant was billed split into entry
// fee, transaction fee (Stripe surcharge), and non-member fee. Renders even
// when individual lines are 0 so the math is visible to admins answering
// "where did this $X come from" questions. The total is highlighted because
// it should match the Fee column on the row above.
function FeeBreakdownPanel({ entry }: { entry: AdminEntryRow }) {
  const fb = entry.fee_breakdown;
  // Fall back to amount_due when the backend hasn't yet populated the
  // breakdown — better to show "—" than nothing.
  if (!fb) {
    return (
      <div className="rounded-md border border-border bg-card/60 p-3">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
          <CircleDollarSign className="h-4 w-4" />
          <span>Fee breakdown</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Detail not available for this entry.
        </p>
      </div>
    );
  }
  // Hide rows that don't apply so the panel stays scannable on the common
  // member-paying-online case (entry fee + transaction fee only).
  const lines: Array<{ label: string; value: number; hint?: string }> = [
    { label: 'Entry fee', value: fb.entry_fee },
    {
      label: 'Transaction fee',
      value: fb.transaction_fee,
      hint: fb.transaction_fee > 0 ? 'Online payment surcharge.' : undefined,
    },
  ];
  if (fb.non_member_fee > 0) {
    lines.push({
      label: 'Non-member fee',
      value: fb.non_member_fee,
      hint: 'Charged because the contestant is not an active member.',
    });
  }
  return (
    <div className="rounded-md border border-border bg-card/60 p-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        <CircleDollarSign className="h-4 w-4" />
        <span>Fee breakdown</span>
      </div>
      <dl className="space-y-1">
        {lines.map((line) => (
          <div
            key={line.label}
            className="flex items-baseline justify-between gap-3 text-sm"
          >
            <dt className="text-muted-foreground">
              {line.label}
              {line.hint && (
                <span className="block text-xs text-muted-foreground/70">
                  {line.hint}
                </span>
              )}
            </dt>
            <dd className="tabular-nums font-medium">
              {formatCurrency(line.value)}
            </dd>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-3 text-sm pt-2 border-t border-border/60 mt-2">
          <dt className="font-medium">Total</dt>
          <dd className="tabular-nums font-bold">
            {formatCurrency(fb.total)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function DetailCard({
  icon,
  label,
  value,
  hint,
  accentIcon,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint?: ReactNode;
  accentIcon?: ReactNode;
  tone?: 'paid' | 'unpaid';
}) {
  const toneCls =
    tone === 'paid'
      ? 'border-emerald-500/30'
      : tone === 'unpaid'
        ? 'border-amber-500/30'
        : 'border-border';
  return (
    <div
      className={`rounded-md border ${toneCls} bg-card/60 p-3 flex flex-col gap-1`}
    >
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-sm font-medium flex items-center gap-2">
        <span>{value}</span>
        {accentIcon && (
          <span className="text-muted-foreground" aria-hidden>
            {accentIcon}
          </span>
        )}
      </div>
      {hint && (
        <div className="text-xs text-muted-foreground leading-snug">{hint}</div>
      )}
    </div>
  );
}
